// Prayer Requests preview for the dashboard: a glanceable list of the few
// most recent active requests with a View All link. The full workspace
// (filters, categories, mark-answered) lives on the Prayers page — this
// component deliberately duplicates none of it.

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { prayersAPI } from '../api/client';
import type { StructuredPrayerSummary, PrayerSummaryItem } from '../types';
import { Calendar, Heart, Lock, Plus, Users } from 'lucide-react';
import InsetItem, { InsetMeta } from './InsetItem';

const PREVIEW_COUNT = 3;

type PreviewItem = PrayerSummaryItem & { categoryLabel: string };

export default function PrayerOverview() {
  const [data, setData] = useState<StructuredPrayerSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await prayersAPI.getStructuredSummary();
      setData(result);
    } catch (err) {
      console.error('Error loading prayer summary:', err);
      setError('Unable to load prayers');
    } finally {
      setIsLoading(false);
    }
  };

  const allPrayers: PreviewItem[] = (data?.categories || []).flatMap((category) =>
    category.prayers.map((prayer) => ({ ...prayer, categoryLabel: category.label })),
  );
  allPrayers.sort((a, b) => Number(b.is_new) - Number(a.is_new));
  const preview = allPrayers.slice(0, PREVIEW_COUNT);
  const activeCount = allPrayers.length;

  return (
    <div className="bg-white rounded-2xl px-6 py-5 shadow-sm h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs font-medium uppercase tracking-wider text-gray-400">Prayers</p>
        <Link to="/prayers" className="text-sm text-navy hover:underline">
          All prayers
        </Link>
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : error ? (
        <div className="flex-1 flex flex-col">
          <p className="text-sm text-gray-500">{error}</p>
          <button onClick={loadData} className="mt-2 text-sm text-navy hover:underline text-left">
            Try again
          </button>
        </div>
      ) : preview.length === 0 ? (
        <div className="flex-1 flex flex-col">
          <p className="text-lg font-semibold text-navy">No active prayer requests</p>
          <p className="text-sm text-gray-500 mt-1">
            Add one for yourself, or share a request with your forum.
          </p>
          <Link
            to="/prayers?new=1"
            className="inline-flex items-center gap-1 mt-auto pt-4 text-sm font-medium text-navy hover:underline"
          >
            <Plus size={14} />
            Add a prayer request
          </Link>
        </div>
      ) : (
        <div className="flex-1 flex flex-col">
          <div className="space-y-3">
            {preview.map((prayer) => (
              <InsetItem
                key={prayer.id}
                title={
                  <>
                    <Link
                      to={prayer.snapshot_id ? `/snapshots/${prayer.snapshot_id}` : '/prayers'}
                      className="hover:underline"
                    >
                      {prayer.title}
                    </Link>
                    {prayer.is_new && (
                      <span className="ml-2 inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium bg-gold/15 text-gold align-middle">
                        New
                      </span>
                    )}
                    {prayer.is_private && (
                      <Lock size={12} className="inline ml-1.5 text-gray-400 align-middle" />
                    )}
                  </>
                }
                meta={
                  <>
                    <InsetMeta icon={Users}>
                      {prayer.is_own ? 'You' : prayer.user_name}
                    </InsetMeta>
                    {prayer.praying_for_display && (
                      <InsetMeta>For {prayer.praying_for_display}</InsetMeta>
                    )}
                    <InsetMeta>{prayer.categoryLabel}</InsetMeta>
                    {prayer.created_at && (
                      <InsetMeta icon={Calendar}>
                        {new Date(prayer.created_at).toLocaleDateString(undefined, {
                          month: 'short',
                          day: 'numeric',
                        })}
                      </InsetMeta>
                    )}
                  </>
                }
              />
            ))}
          </div>
          {activeCount > PREVIEW_COUNT && (
            <p className="text-xs text-gray-400 mt-3">
              +{activeCount - PREVIEW_COUNT} more
            </p>
          )}
          <Link
            to="/prayers"
            className="inline-flex items-center gap-1 mt-auto pt-4 text-sm font-medium text-navy hover:underline"
          >
            <Heart size={14} />
            Open prayers
          </Link>
        </div>
      )}
    </div>
  );
}
